import { z } from 'zod';
import { Uuid } from './common.js';

/**
 * Lot AZ « Le prévenir » (décisions de Maxime du 19/10/2026) : quand on ajoute une tâche pour quelqu'un d'autre,
 * ou qu'on lui passe une carte, on peut le prévenir par Google Chat (message privé envoyé au nom de la personne
 * qui coche) ou par mail (depuis la boîte partagée, à son adresse Evolusite). Le destinataire est TOUJOURS la
 * personne à qui la carte est attribuée, membre actif de l'équipe : il n'est jamais lu dans la demande.
 */

/** Canal choisi : Google Chat (par défaut) ou mail. */
export const TaskNotifyChannel = z.enum(['chat', 'email']);
export type TaskNotifyChannel = z.infer<typeof TaskNotifyChannel>;

export const TASK_NOTIFY_CHANNEL_LABELS: Record<TaskNotifyChannel, string> = {
  chat: 'Google Chat',
  email: 'Mail',
};

/** Longueur maximale du message (le lien vers la tâche est ajouté après). */
export const TASK_NOTIFY_MESSAGE_MAX = 2_000;

/**
 * `POST /api/actions/:id/notify` : prévenir la personne de la carte. Réservé au réseau (jamais gardé hors
 * connexion). Le message est celui affiché et modifiable ; le serveur ajoute le lien vers la carte à la fin.
 */
export const TaskNotifyBody = z.object({
  channel: TaskNotifyChannel,
  message: z.string().trim().min(1).max(TASK_NOTIFY_MESSAGE_MAX),
});
export type TaskNotifyBody = z.infer<typeof TaskNotifyBody>;

export const TaskNotifyResult = z.object({
  channel: TaskNotifyChannel,
  recipientId: Uuid,
  recipientName: z.string(),
});
export type TaskNotifyResult = z.infer<typeof TaskNotifyResult>;

/** Un canal utilisable, ou la phrase qui dit pourquoi il est grisé. */
export const TaskNotifyChannelState = z.object({
  available: z.boolean(),
  reason: z.string().nullable(),
});
export type TaskNotifyChannelState = z.infer<typeof TaskNotifyChannelState>;

/**
 * `GET /api/me/task-notify` : canaux utilisables par la personne connectée (Chat : son compte Google relié avec
 * les autorisations Chat ; mail : boîte partagée prête) et dernier canal choisi (`preferences.taskNotifyChannel`).
 */
export const TaskNotifyOptions = z.object({
  chat: TaskNotifyChannelState,
  email: TaskNotifyChannelState,
  lastChannel: TaskNotifyChannel,
});
export type TaskNotifyOptions = z.infer<typeof TaskNotifyOptions>;
